import { useEffect, useState } from "react";
import { isBoardName, itemTitle } from "./atelier";
import { currentUserId } from "./auth";
import { listCoffre, loadFicheNotes, noteVisibleTo } from "./coffre";
import { listDocuments } from "./facturx";
import { listSignDocs } from "./sign";
import type { CrmFiche } from "./types";
import { kanbanBoardName, loadWeekTasks } from "./week";

export type FindKind = "Fiche" | "Tâche" | "Note" | "Kanban" | "Devis" | "Facture" | "Signature";

export type FindHit = {
  id: string;
  kind: FindKind;
  title: string;
  detail: string;
  ficheId: number;
  taskId: string;
  noteHeading: string;
  boardPath: string;
  signId: number;
  docId: number;
};

const EMPTY = { taskId: "", noteHeading: "", boardPath: "", signId: 0, docId: 0 };

let cache: FindHit[] | null = null;
let cacheKey = "";
let cacheAt = 0;
let inflight: Promise<FindHit[]> | null = null;
let inflightKey = "";
let aimed: FindHit | null = null;

export function aimFind(hit: FindHit): void {
  aimed = hit;
}

export function aimedFind(kind: FindKind, ficheId: number): FindHit | null {
  if (!aimed || aimed.kind !== kind || aimed.ficheId !== ficheId) return null;
  return aimed;
}

export function clearFind(): void {
  aimed = null;
}

export async function findHits(fiches: CrmFiche[], query: string): Promise<FindHit[]> {
  const needle = fold(query);
  if (needle.length < 2) return [];
  const rows = await corpus(fiches);
  return rows.filter((row) => fold(`${row.kind} ${row.title} ${row.detail}`).includes(needle)).slice(0, 14);
}

export function Finder({ query, fiches, onPick }: { query: string; fiches: CrmFiche[]; onPick: (hit: FindHit) => void }) {
  const [hits, setHits] = useState<FindHit[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    const timer = window.setTimeout(() => {
      void findHits(fiches, query)
        .then((rows) => { if (alive) setHits(rows); })
        .catch(() => { if (alive) setHits([]); })
        .finally(() => { if (alive) setBusy(false); });
    }, 160);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [query, fiches]);

  useEffect(() => {
    function down(event: KeyboardEvent) {
      if (event.key === "Enter" && hits[0]) onPick(hits[0]);
    }
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [hits, onPick]);

  return (
    <div className="crm-seek-panel absolute left-1/2 top-[calc(100%+0.45rem)] z-30 max-h-80 w-full -translate-x-1/2 overflow-auto rounded-2xl py-1">
      {busy && hits.length === 0 ? <SeekPulse /> : null}
      {!busy && hits.length === 0 ? <p className="px-3 py-3 text-center text-sm text-slate-500">Aucun résultat.</p> : null}
      {hits.map((hit) => (
        <button key={hit.id} type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-black/5" onMouseDown={(event) => event.preventDefault()} onClick={() => onPick(hit)}>
          <span className="crm-pill shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold">{hit.kind}</span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{hit.title}</span>
            {hit.detail ? <span className="block truncate text-xs text-slate-500">{hit.detail}</span> : null}
          </span>
        </button>
      ))}
    </div>
  );
}

async function corpus(fiches: CrmFiche[]): Promise<FindHit[]> {
  const key = fiches.map((fiche) => `${fiche.id}:${fiche.name}:${fiche.coffre_path}:${fiche.sign_folder_id ?? 0}:${fiche.facturx_client_id}`).join("|");
  if (cache && cacheKey === key && Date.now() - cacheAt < 20000) return cache;
  if (inflight && inflightKey === key) return inflight;
  inflightKey = key;
  inflight = build(fiches).then((rows) => {
    cache = rows;
    cacheKey = key;
    cacheAt = Date.now();
    inflight = null;
    return rows;
  });
  return inflight;
}

async function build(fiches: CrmFiche[]): Promise<FindHit[]> {
  const hits: FindHit[] = fiches.map((fiche) => ({
    id: `fiche-${fiche.id}`,
    kind: "Fiche",
    title: fiche.name,
    detail: [fiche.code, fiche.email, fiche.phone, fiche.city].filter(Boolean).join(" · "),
    ficheId: fiche.id,
    ...EMPTY,
  }));
  const [tasks, signs] = await Promise.all([
    loadWeekTasks().catch(() => []),
    listSignDocs(0).catch(() => []),
  ]);
  tasks.forEach((task) => {
    const linked = Number(task.meta.crmFicheId) || fiches.find((fiche) => fiche.name.trim().toLowerCase() === task.client_name.trim().toLowerCase())?.id || 0;
    hits.push({
      id: `task-${task.id}`,
      kind: "Tâche",
      title: task.title,
      detail: [task.client_name, kanbanBoardName(task.meta) ? `Kanban · ${kanbanBoardName(task.meta)}` : ""].filter(Boolean).join(" · ") || "Tâche",
      ficheId: linked,
      ...EMPTY,
      taskId: task.id,
    });
  });
  signs.forEach((doc) => {
    const matches = fiches.filter((fiche) => (fiche.sign_folder_id ?? 0) === doc.folder_id);
    const fiche = matches.length === 1 ? matches[0] : matches.find((row) => fold(row.name) === fold(doc.folder)) || null;
    if (!fiche) return;
    hits.push({
      id: `sign-${doc.id}`,
      kind: "Signature",
      title: doc.title,
      detail: fiche.name,
      ficheId: fiche.id,
      ...EMPTY,
      signId: doc.id,
    });
  });
  await Promise.all(fiches.map(async (fiche) => {
    if (fiche.coffre_path) {
      const notes = await loadFicheNotes(fiche.coffre_path, fiche.coffre_owner_id || 0).catch(() => null);
      const sharedTasks = new Set(tasks.map((task) => task.id));
      notes?.notes.forEach((note) => {
        const guest = (fiche.coffre_owner_id || 0) > 0;
        if (!noteVisibleTo(note, Number(currentUserId()) || 0, guest, guest ? sharedTasks : null)) return;
        const title = note.heading.trim() || note.body.trim().slice(0, 80);
        if (!title) return;
        hits.push({
          id: `note-${fiche.id}-${note.id}`,
          kind: "Note",
          title,
          detail: fiche.name,
          ficheId: fiche.id,
          ...EMPTY,
          noteHeading: note.heading,
        });
      });
      const entries = await listCoffre(fiche.coffre_path, false, fiche.coffre_owner_id || 0).catch(() => []);
      const nested = await Promise.all(entries.filter((entry) => entry.kind === "folder").map((entry) => listCoffre(entry.path, false, fiche.coffre_owner_id || 0).catch(() => [])));
      [...entries, ...nested.flat()].forEach((entry) => {
        if (entry.kind === "folder" || !isBoardName(entry.name)) return;
        hits.push({
          id: `board-${entry.path}`,
          kind: "Kanban",
          title: itemTitle(entry.name),
          detail: fiche.name,
          ficheId: fiche.id,
          ...EMPTY,
          boardPath: entry.path,
        });
      });
    }
    if (fiche.facturx_company_id > 0 && fiche.facturx_client_id > 0) {
      const data = await listDocuments(fiche.facturx_company_id, fiche.facturx_client_id).catch(() => ({ documents: [] }));
      (data.documents || []).forEach((doc) => {
        if (doc.doc_type !== "quote" && doc.doc_type !== "invoice") return;
        const quote = doc.doc_type === "quote";
        hits.push({
          id: `doc-${fiche.id}-${doc.id}`,
          kind: quote ? "Devis" : "Facture",
          title: doc.number || (quote ? "Devis" : "Facture"),
          detail: fiche.name,
          ficheId: fiche.id,
          ...EMPTY,
          docId: doc.id,
        });
      });
    }
  }));
  return hits;
}

function SeekPulse() {
  const words = ["fiches", "tâches", "notes", "kanbans", "devis", "factures"];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % words.length), 1100);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <p className="crm-seek-pulse">
      <span className="crm-seek-spin" aria-hidden />
      <span>Recherche des {words[index]}</span>
    </p>
  );
}

function fold(value: string): string {
  return value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}
